import { useState, useEffect, useCallback, useRef } from "react";
import {
  type TechnicalDocumentAnalysis,
  type PreliminaryBoqLine,
  getTechnicalDocumentAnalysis,
  importBoqLine,
  updateBoqLine,
} from "../technicalDocuments/technicalDocumentsApi";
import {
  getBudgetAreaTypes,
  getBudgetChapterTypes,
  type BudgetAreaType,
  type BudgetChapterType,
} from "../orcamentos/orcamentosApi";

interface BoqValidationViewProps {
  budgetId: number;
  documentId: number;
  onClose: () => void;
  onBudgetChanged?: () => void;
}

const methodBadgeColor: Record<string, string> = {
  extracted: "bg-green-500/10 text-green-700 border border-green-500/30",
  calculated: "bg-amber-500/10 text-amber-700 border border-amber-500/30",
  estimated: "bg-blue-500/10 text-blue-700 border border-blue-500/30",
};

/* ── ComboBox ──────────────────────────────────────────────── */

function ComboBox({
  value,
  options,
  placeholder,
  onChange,
  maxWidth,
}: {
  value: string;
  options: string[];
  placeholder: string;
  onChange: (v: string) => void;
  maxWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setTyping(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = typing && value
    ? options.filter((o) => o.toLowerCase().includes(value.toLowerCase()))
    : options;

  return (
    <div ref={containerRef} className="relative" style={{ maxWidth }}>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setTyping(true);
          setOpen(true);
        }}
        onFocus={() => { setTyping(false); setOpen(true); }}
        className="w-full px-1.5 py-1 text-xs border border-border rounded bg-background"
        style={{ borderRadius: "var(--radius)", maxWidth }}
      />
      {open && filtered.length > 0 && (
        <ul
          className="absolute z-50 mt-0.5 w-full bg-background border border-border shadow-md overflow-auto"
          style={{ borderRadius: "var(--radius)", maxHeight: 180, minWidth: maxWidth }}
        >
          {filtered.map((opt) => (
            <li
              key={opt}
              className="px-2 py-1.5 text-xs cursor-pointer hover:bg-accent text-foreground"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(opt);
                setOpen(false);
                setTyping(false);
              }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── BoqValidationView ─────────────────────────────────────── */

export default function BoqValidationView({
  budgetId,
  documentId,
  onClose,
  onBudgetChanged,
}: BoqValidationViewProps) {
  const [analysis, setAnalysis] = useState<TechnicalDocumentAnalysis | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingLine, setEditingLine] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    description: "",
    quantity: 0,
    unit: "",
  });
  const [actionLoading, setActionLoading] = useState(false);

  const [areaTypes, setAreaTypes] = useState<BudgetAreaType[]>([]);
  const [chapterTypes, setChapterTypes] = useState<BudgetChapterType[]>([]);
  const [lineSelections, setLineSelections] = useState<
    Record<number, { areaName: string; chapterName: string }>
  >({});

  const loadAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTechnicalDocumentAnalysis(budgetId, documentId);
      setAnalysis(data);
    } catch {
      setError("Erro ao carregar análise técnica.");
    } finally {
      setLoading(false);
    }
  }, [budgetId, documentId]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  useEffect(() => {
    Promise.all([getBudgetAreaTypes(), getBudgetChapterTypes()]).then(
      ([areas, chapters]) => {
        setAreaTypes(areas);
        setChapterTypes(chapters);
      },
    );
  }, []);

  // Pre-populate line selections from AI suggestions only
  useEffect(() => {
    if (!analysis || areaTypes.length === 0 || chapterTypes.length === 0) return;

    const suggestions: Record<number, { areaName: string; chapterName: string }> = {};

    for (const line of analysis.boqLines) {
      if (line.isApproved) continue;
      if (!line.suggestedAreaName) continue;

      const area = areaTypes.find(
        (a) => a.name.toLowerCase() === line.suggestedAreaName!.toLowerCase(),
      );

      let areaName = "";
      let chapterName = "";

      if (area) {
        areaName = area.name;

        if (line.suggestedChapterName) {
          const chapter = chapterTypes.find(
            (c) =>
              c.budgetAreaTypeId === area.id &&
              c.name.toLowerCase() === line.suggestedChapterName!.toLowerCase(),
          );
          if (chapter) {
            chapterName = chapter.name;
          }
        }

        if (!chapterName) {
          const fallback = chapterTypes.filter(
            (c) => c.budgetAreaTypeId === area.id,
          );
          chapterName = fallback.length > 0 ? fallback[0].name : "";
        }
      } else {
        // AI suggested an area not in the template — keep it as free text
        areaName = line.suggestedAreaName;
        chapterName = line.suggestedChapterName ?? "";
      }

      suggestions[line.id] = { areaName, chapterName };
    }

    setLineSelections((prev) => {
      const merged = { ...prev };
      for (const [idStr, sel] of Object.entries(suggestions)) {
        const id = Number(idStr);
        if (!merged[id] || (!merged[id].areaName && !merged[id].chapterName)) {
          merged[id] = sel;
        }
      }
      return merged;
    });

  }, [analysis, areaTypes, chapterTypes]);

  const getLineSelection = (lineId: number) => {
    return lineSelections[lineId] ?? { areaName: "", chapterName: "" };
  };

  const updateLineSelection = (
    lineId: number,
    field: "areaName" | "chapterName",
    value: string,
  ) => {
    setLineSelections((prev) => {
      const current = prev[lineId] ?? { areaName: "", chapterName: "" };
      const updated = { ...current, [field]: value };
      // When picking a known area, auto-fill first chapter of that area
      if (field === "areaName") {
        const area = areaTypes.find((a) => a.name === value);
        if (area) {
          const chapters = chapterTypes.filter(
            (c) => c.budgetAreaTypeId === area.id,
          );
          updated.chapterName = chapters.length > 0 ? chapters[0].name : "";
        }
      }
      return { ...prev, [lineId]: updated };
    });
  };

  const getChapterOptionsForArea = (areaName: string): string[] => {
    const area = areaTypes.find(
      (a) => a.name.toLowerCase() === areaName.toLowerCase(),
    );
    if (!area) return chapterTypes.map((c) => c.name);
    return chapterTypes
      .filter((c) => c.budgetAreaTypeId === area.id)
      .map((c) => c.name);
  };

  const areaOptions = areaTypes.map((a) => a.name);

  const handleImport = async (lineId: number) => {
    const sel = getLineSelection(lineId);
    if (!sel.areaName || !sel.chapterName) {
      setError("Preencha a Área e o Capítulo antes de importar.");
      return;
    }
    setActionLoading(true);
    try {
      await importBoqLine(lineId, {
        areaName: sel.areaName,
        chapterName: sel.chapterName,
      });
      await loadAnalysis();
      onBudgetChanged?.();
    } catch {
      setError("Erro ao importar linha.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleImportAllWithSelections = async () => {
    if (!analysis) return;

    const linesToImport = analysis.boqLines.filter((l) => {
      if (l.isApproved) return false;
      const sel = getLineSelection(l.id);
      return sel.areaName && sel.chapterName;
    });

    if (linesToImport.length === 0) {
      setError("Nenhuma linha tem área e capítulo preenchidos.");
      return;
    }

    setActionLoading(true);
    let imported = 0;
    try {
      for (const line of linesToImport) {
        const sel = getLineSelection(line.id);
        await importBoqLine(line.id, {
          areaName: sel.areaName,
          chapterName: sel.chapterName,
        });
        imported++;
      }
    } catch {
      setError(`Erro ao importar linhas (${imported}/${linesToImport.length} importadas).`);
    } finally {
      await loadAnalysis();
      if (imported > 0) {
        onBudgetChanged?.();
      }
      setActionLoading(false);
    }
  };

  const handleStartEdit = (line: PreliminaryBoqLine) => {
    setEditingLine(line.id);
    setEditForm({
      description: line.description,
      quantity: line.quantity,
      unit: line.unit,
    });
  };

  const handleSaveEdit = async () => {
    if (editingLine === null) return;
    setActionLoading(true);
    try {
      await updateBoqLine(editingLine, editForm);
      setEditingLine(null);
      await loadAnalysis();
    } catch {
      setError("Erro ao atualizar linha.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-secondary-text">
        A carregar análise técnica...
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="p-6 text-center text-secondary-text">
        <p>Análise técnica não disponível ou ainda em processamento.</p>
        <div className="mt-4 flex gap-2 justify-center">
          <button
            onClick={loadAnalysis}
            className="px-4 py-2 bg-primary text-primary-foreground rounded"
            style={{ borderRadius: "var(--radius)" }}
          >
            Recarregar
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded"
            style={{ borderRadius: "var(--radius)" }}
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  const importedCount = analysis.boqLines.filter(
    (l: PreliminaryBoqLine) => l.isApproved,
  ).length;
  const totalCount = analysis.boqLines.length;
  const pendingLines = analysis.boqLines.filter(
    (l: PreliminaryBoqLine) => !l.isApproved,
  );
  const readyToImportCount = pendingLines.filter((l) => {
    const sel = getLineSelection(l.id);
    return sel.areaName && sel.chapterName;
  }).length;
  const missingCount = pendingLines.length - readyToImportCount;

  return (
    <div className="p-4">
      {error && (
        <div
          className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-2"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Fechar
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h3
            className="text-foreground"
            style={{
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-weight-medium)",
            }}
          >
            Importação BOQ — {analysis.statusName}
          </h3>
          <p
            className="text-secondary-text"
            style={{ fontSize: "var(--text-caption)" }}
          >
            {importedCount}/{totalCount} linhas importadas
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border border-border rounded text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            Fechar
          </button>
        </div>
      </div>

      {analysis.isExportReady && (
        <div
          className="mb-4 border border-green-300 bg-green-50 text-green-800 px-4 py-2"
          style={{ borderRadius: "var(--radius)" }}
        >
          Todas as linhas foram importadas para o orçamento.
        </div>
      )}

      {/* Bulk import controls */}
      {pendingLines.length > 0 && (
        <div
          className="mb-4 p-3 border border-border bg-secondary/30 flex items-center gap-3 flex-wrap"
          style={{ borderRadius: "var(--radius)" }}
        >
          {readyToImportCount > 0 && (
            <button
              onClick={handleImportAllWithSelections}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-sm disabled:opacity-60"
              style={{ borderRadius: "var(--radius)" }}
            >
              {actionLoading ? 'A importar…' : `Importar ${readyToImportCount} ${readyToImportCount === 1 ? 'linha identificada' : 'linhas identificadas'}`}
            </button>
          )}
          {missingCount > 0 && (
            <span className="text-secondary-text text-sm">
              {missingCount} {missingCount === 1 ? 'linha sem' : 'linhas sem'} área/capítulo — preencha manualmente
            </span>
          )}
        </div>
      )}

      <div className="space-y-2">
        {analysis.boqLines.map((line: PreliminaryBoqLine) => {
          const sel = getLineSelection(line.id);
          const chapterOptions = getChapterOptionsForArea(sel.areaName);

          return (
            <div
              key={line.id}
              className={`p-3 border rounded ${
                line.isApproved
                  ? "border-green-300 bg-green-50/50"
                  : "border-border bg-background"
              }`}
              style={{ borderRadius: "var(--radius)" }}
            >
              {editingLine === line.id ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({ ...editForm, description: e.target.value })
                    }
                    className="w-full px-2 py-1 border border-border rounded text-sm"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.quantity}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          quantity: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-24 px-2 py-1 border border-border rounded text-sm"
                      style={{ borderRadius: "var(--radius)" }}
                    />
                    <input
                      type="text"
                      value={editForm.unit}
                      onChange={(e) =>
                        setEditForm({ ...editForm, unit: e.target.value })
                      }
                      className="w-20 px-2 py-1 border border-border rounded text-sm"
                      style={{ borderRadius: "var(--radius)" }}
                    />
                    <button
                      onClick={handleSaveEdit}
                      disabled={actionLoading}
                      className="px-3 py-1 bg-primary text-primary-foreground rounded text-sm disabled:opacity-60"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      Guardar
                    </button>
                    <button
                      onClick={() => setEditingLine(null)}
                      className="px-3 py-1 border border-border rounded text-sm"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-foreground"
                      style={{ fontSize: "var(--text-base)" }}
                    >
                      {line.description}
                    </div>
                    <div
                      className="flex items-center gap-3 mt-1 text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      <span>
                        {line.quantity} {line.unit}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs ${
                          methodBadgeColor[line.measurementMethodCode] ??
                          "bg-gray-500/10 text-gray-700 border border-gray-500/30"
                        }`}
                      >
                        {line.measurementMethodName}
                      </span>
                      {line.templateLineCode && (
                        <span className="text-xs text-muted-foreground">
                          Template: {line.templateLineCode}
                        </span>
                      )}
                      {line.isApproved && (
                        <span className="text-green-600 text-xs">
                          Importado
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!line.isApproved && (
                      <>
                        <ComboBox
                          value={sel.areaName}
                          options={areaOptions}
                          placeholder="Área"
                          maxWidth={130}
                          onChange={(v) =>
                            updateLineSelection(line.id, "areaName", v)
                          }
                        />
                        <ComboBox
                          value={sel.chapterName}
                          options={chapterOptions}
                          placeholder="Capítulo"
                          maxWidth={160}
                          onChange={(v) =>
                            updateLineSelection(line.id, "chapterName", v)
                          }
                        />
                        <button
                          onClick={() => handleImport(line.id)}
                          disabled={
                            actionLoading ||
                            !sel.areaName ||
                            !sel.chapterName
                          }
                          className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-60"
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          Importar
                        </button>
                        <button
                          onClick={() => handleStartEdit(line)}
                          className="px-2 py-1 text-xs border border-border rounded hover:bg-secondary"
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          Editar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
